import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Card,
  DatePicker,
  Button,
  Table,
  Tag,
  Space,
  message,
  Spin,
  Empty,
  Select,
  Row,
  Col,
  Statistic,
  Tooltip,
  Form,
  Typography,
  Result,
  Checkbox
} from 'antd'
import {
  CalendarOutlined,
  ReloadOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  InfoCircleOutlined,
  BankOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import { now as tzNow } from '../utils/timezone'

const { Text } = Typography
const { Option } = Select
const { RangePicker } = DatePicker

// Premium color palette
const colors = {
  primary: '#2C3E50',
  secondary: '#5D7A9B',
  accent: '#8B9DC3',
  text: {
    primary: '#1A1A1A',
    secondary: '#6B7280',
    tertiary: '#9CA3AF'
  },
  background: {
    primary: '#FFFFFF',
    secondary: '#FAFBFC',
    tertiary: '#F5F7FA'
  },
  border: '#E5E7EB',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  // 支出結構圓餅圖用：與既有藍灰色系同調，但足以與其他扇形區分
  info: '#6366F1'
}

const spacing = {
  xs: '8px',
  sm: '16px',
  md: '24px',
  lg: '32px',
  xl: '48px',
  xxl: '64px'
}

const PageContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: ${spacing.lg} ${spacing.md};
  background: ${colors.background.secondary};
  min-height: 100vh;

  @media (max-width: 820px) {
    padding: ${spacing.md} ${spacing.sm};
  }
`

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 300;
  color: ${colors.text.primary};
  margin: 0 0 ${spacing.xl} 0;
  letter-spacing: -0.5px;

  @media (max-width: 820px) {
    font-size: 24px;
    margin-bottom: ${spacing.lg};
  }
`

const FilterCard = styled(Card)`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: ${spacing.lg};

  .ant-card-body {
    padding: ${spacing.lg};

    @media (max-width: 820px) {
      padding: ${spacing.md};
    }
  }
`

const SummaryCard = styled(Card)`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: ${spacing.lg};

  .ant-card-body {
    padding: ${spacing.lg};

    @media (max-width: 820px) {
      padding: ${spacing.md};
    }
  }
`

const SummaryRow = styled(Row)`
  margin-bottom: ${spacing.md};

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionCard = styled(Card)`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  margin-bottom: ${spacing.lg};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  .ant-card-head {
    border-bottom: 1px solid ${colors.border};
    padding: 8px 16px;

    .ant-card-head-title {
      font-size: 18px;
      font-weight: 500;
      color: ${colors.text.primary};
    }
  }

  .ant-card-body {
    padding: ${spacing.lg};
    overflow: visible;

    @media (max-width: 820px) {
      padding: ${spacing.md};
    }
  }
`

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`

const TableContainer = styled.div`
  overflow-x: auto;

  .ant-table {
    font-size: 14px;
  }

  .ant-table-thead > tr > th {
    background: ${colors.background.secondary};
    color: ${colors.text.primary};
    font-weight: 500;
    border-bottom: 1px solid ${colors.border};
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${colors.border};
  }

  .ant-table-tbody > tr:hover > td {
    background: ${colors.background.tertiary};
  }
`

const AmountCell = styled.div`
  text-align: right;
  font-weight: 500;
  color: ${(props) => (props.negative ? colors.error : colors.text.primary)};
`

const CategoryTag = styled(Tag)`
  border-radius: 4px;
  font-size: 12px;
  padding: 2px ${spacing.xs};
`

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.md};
  align-items: center;

  @media (max-width: 820px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0'
  const numeric = Number(value) || 0
  return `$${numeric.toLocaleString()}`
}

const RevenueRecordsPage = () => {
  const { user } = useAuth()
  const { hasFeature, permissionsLoaded } = useMembership()
  const { can } = usePermission()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState('daily') // 'daily', 'monthly', 'yearly'
  const [selectedDate, setSelectedDate] = useState(tzNow())
  const [revenueData, setRevenueData] = useState(null)
  const initializedRef = useRef(false)

  const canViewFinance = can('finance:view')
  // 只有財務管理權限能改「營業成本包含哪些項目」—— 這會影響全店所有人看到的盈餘
  const canEditCostItems = can('finance:manage')
  const [savingCostItems, setSavingCostItems] = useState(false)

  const fetchRevenueData = useCallback(async () => {
    if (!user?.businessId || !hasFeature('checkout')) return
    if (loadingRef.current) return

    loadingRef.current = true
    setLoading(true)

    try {
      let startDate, endDate
      const date = selectedDate || tzNow()

      switch (dateRange) {
        case 'daily':
          startDate = date.format('YYYY-MM-DD')
          endDate = date.format('YYYY-MM-DD')
          break
        case 'monthly':
          startDate = date.startOf('month').format('YYYY-MM-DD')
          endDate = date.endOf('month').format('YYYY-MM-DD')
          break
        case 'yearly':
          startDate = date.startOf('year').format('YYYY-MM-DD')
          endDate = date.endOf('year').format('YYYY-MM-DD')
          break
        default:
          startDate = date.format('YYYY-MM-DD')
          endDate = date.format('YYYY-MM-DD')
      }

      const response = await api.get(
        `/api/reports/${user.businessId}/revenue/records`,
        {
          params: {
            startDate,
            endDate,
            range: dateRange
          }
        }
      )

      setRevenueData(response.data?.data || response.data || null)
    } catch (error) {
      console.error('獲取營收記錄失敗:', error)
      message.error(
        error.response?.data?.message ||
          error.message ||
          '無法獲取營收記錄，請稍後再試'
      )
      setRevenueData(null)
    } finally {
      setLoading(false)
      loadingRef.current = false
    }
  }, [user?.businessId, dateRange, selectedDate, hasFeature])

  const loadingRef = useRef(false)

  useEffect(() => {
    if (!hasFeature('checkout') || initializedRef.current) return
    initializedRef.current = true
    fetchRevenueData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!hasFeature('checkout')) return
    fetchRevenueData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedDate])

  /**
   * 切換某個成本項目要不要計入營業成本。
   *
   * 設定存在商家上（整店統一），所以存完要重新拉報表 —— 後端會依新設定重算
   * 營業成本與本期盈餘，前端不自己算，避免兩邊公式走鐘。
   */
  const handleToggleCostItem = async (key, checked) => {
    if (!canEditCostItems || savingCostItems) return
    setSavingCostItems(true)
    try {
      await api.put(`/api/reports/${user.businessId}/cost-settings`, {
        costItems: { [key]: checked }
      })
      await fetchRevenueData()
      message.success(checked ? '已計入營業成本' : '已排除於營業成本')
    } catch (error) {
      console.error('更新營業成本項目設定失敗:', error)
      message.error(
        error.response?.data?.message || '更新失敗，請稍後再試'
      )
    } finally {
      setSavingCostItems(false)
    }
  }

  const handleDateRangeChange = (value) => {
    setDateRange(value)
  }

  const handleDateChange = (date) => {
    setSelectedDate(date || tzNow())
  }

  const handleRefresh = () => {
    fetchRevenueData()
  }

  if (!permissionsLoaded) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size='large' tip='正在載入權限...' />
        </div>
      </PageContainer>
    )
  }

  if (!hasFeature('checkout')) {
    return (
      <PageContainer>
        <Card>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <p style={{ fontSize: '16px', color: colors.text.secondary }}>
              此功能需要更高級別的會員方案才能使用
            </p>
            <Button
              type='primary'
              onClick={() => navigate('/settings/membership')}
              style={{ marginTop: '16px' }}
            >
              查看會員方案
            </Button>
          </div>
        </Card>
      </PageContainer>
    )
  }

  // 权限检查：需要 finance:view 权限
  if (!canViewFinance) {
    return (
      <PageContainer>
        <Card>
          <Result
            status='403'
            title='權限不足'
            subTitle='您沒有權限查看營收紀錄。此功能需要財務檢視權限，請聯繫管理員為您分配相應權限。'
            extra={
              <Button type='primary' onClick={() => navigate('/')}>
                返回首頁
              </Button>
            }
          />
        </Card>
      </PageContainer>
    )
  }

  // 依平台現有內容整理：店家有沒有賺錢 + 預收款 + 細項明細
  const prepaymentReceived = Number(
    revenueData?.prepayment?.received?.total ?? 0
  )
  // 儲值金餘額：報表篩選區間的 endDate 當日餘額（後端依 endDate 算）
  const prepaymentBalance = Number(revenueData?.prepayment?.balance ?? 0)
  const prepaymentUsed =
    Number(revenueData?.prepayment?.usedInPeriod ?? 0) ||
    Number(revenueData?.expense?.storedValueDeduction?.nonCash ?? 0) ||
    Number(revenueData?.expense?.storedValueDeduction?.total ?? 0)

  const income = revenueData?.income || {}
  const expense = revenueData?.expense || {}
  const summary = revenueData?.summary || {}
  const calculation = revenueData?.calculation || {}

  // 技術服務：{ cash, nonCash }
  const techCash = Number(income.technology?.cash ?? 0)
  const techNonCash = Number(income.technology?.nonCash ?? 0)
  const techIncome = techCash + techNonCash

  // 商品銷售：storeSales / productSales 皆為 { cash, nonCash }，需加總
  const storeSalesVal =
    Number(income.storeSales?.cash ?? 0) +
    Number(income.storeSales?.nonCash ?? 0)
  const productSalesVal =
    Number(income.productSales?.cash ?? 0) +
    Number(income.productSales?.nonCash ?? 0)
  const productIncome = storeSalesVal + productSalesVal

  // 堂票銷售（manual + checkout，依 issuedAt 付款當天認列）
  const classTicketIncome =
    Number(income.classTicket?.cash ?? 0) +
    Number(income.classTicket?.nonCash ?? 0)

  // 營業收入 = 技術服務 + 商品銷售 + 堂票銷售
  const operatingRevenueCalc = techIncome + productIncome + classTicketIncome
  const operatingRevenue =
    Number(calculation.operatingRevenue?.total) ??
    Number(income.operatingRevenue) ??
    Number(summary.totalIncome) ??
    operatingRevenueCalc

  const totalExpense =
    Number(calculation.totalExpense?.total) ??
    (summary.totalExpense != null
      ? Number(summary.totalExpense)
      : Number(expense.total ?? 0) +
        Number(expense.manualCash ?? 0) +
        Number(expense.manualNonCash ?? 0))
  const businessRevenue = operatingRevenue

  // 支出細項
  const productPurchaseCost =
    Number(expense.purchase?.cash ?? 0) + Number(expense.purchase?.nonCash ?? 0)
  // expense.refund 後端回傳為 {cash, nonCash} 物件，不可整體 Number() 轉換（會得到 NaN，導致 otherExpense 變 NaN）
  const refundAmount =
    Number(expense.refund?.cash ?? 0) +
    Number(expense.refund?.nonCash ?? 0)
  const salaryExpense =
    Number(expense.salary?.cash ?? 0) +
    Number(expense.salary?.nonCash ?? 0) +
    Number(expense.salary ?? 0)
  // 支出明細無「匯款」項目；折扣不是支出，不列

  // 關係說明用：篩選區間與 endDate 標籤
  const date = selectedDate || tzNow()
  const rangeLabel =
    dateRange === 'daily'
      ? date.format('YYYY-MM-DD')
      : dateRange === 'monthly'
        ? `${date.format('YYYY')} 年 ${date.format('M')} 月`
        : `${date.format('YYYY')} 年`
  const endDateStr =
    dateRange === 'daily'
      ? date.format('M/D')
      : dateRange === 'monthly'
        ? date.endOf('month').format('M/D')
        : date.endOf('year').format('M/D')
  // 餘額的加減要用「面額」（含贈送），才和「使用的儲值金」同基準；
  // prepaymentReceived（實收）是金流視角，兩者在有贈送時不相等。
  const prepaymentReceivedFace = Number(
    revenueData?.prepayment?.received?.faceValue ?? prepaymentReceived
  )
  const prepaymentBonus = Math.max(
    0,
    prepaymentReceivedFace - prepaymentReceived
  )
  const prepaymentNetChange = prepaymentReceivedFace - prepaymentUsed

  // 預收款獨立區塊：收到的儲值金、使用的儲值金、儲值金餘額（篩選區間 endDate 當日）
  const prepaymentRows = [
    {
      key: 'received',
      name: '收到的儲值金',
      amount: prepaymentReceived,
      info:
        '本期（篩選區間內）客戶「購買儲值」實際付的錢' +
        (prepaymentBonus > 0
          ? `。含贈送後客戶餘額共增加 ${prepaymentReceivedFace} 元（贈送 ${prepaymentBonus} 元）`
          : '')
    },
    {
      key: 'used',
      name: '使用的儲值金',
      amount: prepaymentUsed,
      info: '本期（篩選區間內）客戶「用儲值金消費」的金額'
    },
    {
      key: 'balance',
      name: '儲值金餘額',
      amount: prepaymentBalance,
      info: '到 選擇的日期 當天為止的累計餘額。公式：餘額 = 期初餘額 + 本期收到 − 本期使用（餘額 ≠ 本期收到 − 本期使用，需加期初餘額）'
    }
  ]

  // 技術服務收入－支付組成（圓餅圖用）
  const paymentComposition = [
    { name: '現金', value: techCash, color: colors.primary },
    { name: '非現金', value: techNonCash, color: colors.secondary }
  ].filter((d) => d.value > 0)
  const hasPaymentData = paymentComposition.length > 0

  // 收入來源占比：營業收入 = 技術服務 + 商品銷售；可含客戶購買儲值（預收款）
  const incomeSourceComposition = [
    {
      name: '技術服務',
      value: Number(calculation.operatingRevenue?.technology) ?? techIncome,
      color: colors.primary
    },
    {
      name: '商品銷售',
      value: Number(calculation.operatingRevenue?.storeSales) ?? productIncome,
      color: colors.secondary
    },
    {
      name: '堂票銷售',
      value: classTicketIncome,
      color: colors.accent
    },
    { name: '客戶購買儲值', value: prepaymentReceived, color: colors.warning }
  ].filter((d) => d.value > 0)
  const hasIncomeSourceData = incomeSourceComposition.length > 0

  // 金流手續費（刷卡 + 行動支付）— 於後續計算與顯示引用
  const paymentFeeForExpense =
    Number(revenueData?.summary?.paymentFee) ||
    Number(revenueData?.expense?.paymentFee?.total) ||
    0

  // 營業成本項目設定（後端回傳，整店統一）
  const costItemsSetting = calculation.totalExpense?.costItems || {}
  const excludedCostItems = calculation.totalExpense?.excluded || {}
  const isCostItemIncluded = (key) => costItemsSetting[key] !== false
  const COST_ITEM_OPTIONS = [
    { key: 'paymentFee', label: '金流服務費' },
    { key: 'storedValueDiscount', label: '儲值折讓' },
    { key: 'allowance', label: '零用金' },
    { key: 'salary', label: '薪資' }
  ]

  // 儲值折讓：客戶用儲值消費時，贈送額度與儲值折扣造成的收入灌水
  // （儲 10,000 送 2,000，用掉 12,000 時營業收入認列 12,000，但店家只收到 10,000，
  //   多出來的那 2,000 是銷售折讓，屬成本）
  const storedValueDiscount = Number(
    revenueData?.expense?.storedValueDiscount ??
      calculation.totalExpense?.storedValueDiscount ??
      0
  )

  // 零用金支出（零用金頁登記的日常雜支，後端已依交易日期認列）
  // 舊版後端只回傳 manualCash，這裡保留 fallback
  const allowanceExpense =
    Number(revenueData?.expense?.allowance ?? 0) ||
    Number(revenueData?.expense?.manualCash ?? 0) ||
    0

  // 支出細項：後端已算好 other（用「全部計入」的總額推的）。
  // 前端不能再用 totalExpense 去減 —— 店家關掉項目後 totalExpense 會變小，自己推會算成負數。
  const otherExpense = Math.max(
    0,
    Number(
      calculation.totalExpense?.other ??
        totalExpense -
          productPurchaseCost -
          refundAmount -
          storedValueDiscount -
          salaryExpense -
          paymentFeeForExpense -
          allowanceExpense
    )
  )

  // 支出結構占比（數據分析：成本結構）
  // 圓餅圖只放「有計入營業成本」的項目，總和才會等於營業成本
  const expenseComposition = [
    { name: '進貨成本', value: productPurchaseCost, color: colors.primary },
    ...(isCostItemIncluded('storedValueDiscount')
      ? [{ name: '儲值折讓', value: storedValueDiscount, color: colors.secondary }]
      : []),
    { name: '退款', value: refundAmount, color: colors.error },
    ...(isCostItemIncluded('salary')
      ? [{ name: '薪資', value: salaryExpense, color: colors.warning }]
      : []),
    ...(isCostItemIncluded('paymentFee')
      ? [{ name: '金流服務費', value: paymentFeeForExpense, color: colors.error }]
      : []),
    ...(isCostItemIncluded('allowance')
      ? [{ name: '零用金支出', value: allowanceExpense, color: colors.info }]
      : []),
    { name: '其他支出', value: otherExpense, color: colors.accent }
  ].filter((d) => d.value > 0)
  const hasExpenseCompositionData = expenseComposition.length > 0

  // 預收款流動：本期使用率（使用的儲值 ÷ 客戶餘額增加的面額，評估流動健康度）
  // 使用率要拿同基準的兩個數字比：使用的是面額，分母也要用面額（含贈送）
  const prepaymentUseRate =
    prepaymentReceivedFace > 0
      ? ((prepaymentUsed / prepaymentReceivedFace) * 100).toFixed(1)
      : null
  const salaryToRevenue =
    businessRevenue > 0 && salaryExpense > 0
      ? ((salaryExpense / businessRevenue) * 100).toFixed(1)
      : null

  // 收入明細：技術服務 + 商品銷售 + 堂票銷售（客戶購買儲值在預收款區塊）
  const incomeRows = [
    {
      key: 'tech',
      name: '技術服務',
      amount: Number(calculation.operatingRevenue?.technology) ?? techIncome,
      info: '技術等服務的營收（已含折扣後實收）'
    },
    {
      key: 'product',
      name: '商品銷售',
      amount: Number(calculation.operatingRevenue?.storeSales) ?? productIncome,
      info: '店內商品銷售營收（已含折扣後實收），不為業績價格，僅計算商品售價'
    },
    ...(classTicketIncome > 0
      ? [
          {
            key: 'classTicket',
            name: '堂票銷售',
            amount: classTicketIncome,
            info: '手動發放及結帳購買堂票的銷售金額（依付款當天認列）'
          }
        ]
      : [])
  ]

  // 支出明細：完整項目（無「匯款」項目；折扣不是支出、不列）
  const expenseRows = [
    {
      key: 'purchase',
      name: '進貨成本',
      amount: productPurchaseCost,
      info: '商品採購、材料等成本'
    },
    {
      key: 'refund',
      name: '退款',
      amount: refundAmount,
      info: '退給客戶的款項'
    },
    ...(salaryExpense > 0
      ? [
          {
            key: 'salary',
            name: '薪資',
            amount: salaryExpense,
            excluded: !isCostItemIncluded('salary'),
            info: '員工薪資、抽成等'
          }
        ]
      : []),
    // 金流服務費（刷卡、行動支付手續費）
    ...(paymentFeeForExpense > 0
      ? [
          {
            key: 'payment_fee',
            name: '金流服務費',
            amount: paymentFeeForExpense,
            excluded: !isCostItemIncluded('paymentFee'),
            info: '刷卡、行動支付由收單行／第三方支付扣除的手續費'
          }
        ]
      : []),
    // 儲值折讓（贈送額度／儲值折扣造成的收入灌水，屬銷售折讓）
    ...(storedValueDiscount > 0
      ? [
          {
            key: 'stored_value_discount',
            name: '儲值折讓',
            amount: storedValueDiscount,
            excluded: !isCostItemIncluded('storedValueDiscount'),
            info: '客戶用儲值消費時，贈送額度與儲值折扣的部分。這段營業收入有認列、但店家從未收到現金，故列為銷售折讓。客戶使用儲值的總額請看「預收款」區塊。'
          }
        ]
      : []),
    // 零用金支出（原本混在「其他支出」裡，現獨立列出）
    ...(allowanceExpense > 0
      ? [
          {
            key: 'allowance',
            name: '零用金支出',
            amount: allowanceExpense,
            excluded: !isCostItemIncluded('allowance'),
            info: '零用金頁登記的日常雜支（含餘額調整的負數），明細可至「零用金設定」查看'
          }
        ]
      : []),
    {
      key: 'other',
      name: '其他支出',
      amount: otherExpense,
      info: '課程扣抵、回饋等其他成本（不含零用金支出）'
    }
  ]

  // 店家有沒有賺錢：營業收入、營業成本、毛利、本期盈餘
  // 營業成本 = 支出總計（進貨+退款+薪資+金流服務費+零用金+儲值折讓+其他），與 netProfit 公式一致
  // 注意：客戶使用儲值不在成本內 —— 那是預收款兌現，收入已認列、負債同額減少，不是花出去的錢
  const operatingCost =
    Number(calculation.totalExpense?.total) ??
    Number(calculation.operatingCost?.total) ??
    totalExpense
  const grossProfit =
    Number(calculation.grossProfit?.total) ??
    Number(summary.grossProfit) ??
    operatingRevenue - productPurchaseCost
  const netProfit =
    Number(calculation.netProfit?.total) ??
    (summary.netProfit != null ? Number(summary.netProfit) : null) ??
    operatingRevenue - totalExpense

  // === 新增：金流手續費 / 淨營業收入 ===
  // 金流服務費（刷卡+行動支付手續費）：店家實際需負擔的金流成本
  const paymentFee =
    Number(calculation.paymentFee?.total) ||
    Number(summary.paymentFee) ||
    Number(revenueData?.expense?.paymentFee?.total) ||
    0
  const paymentFeeCard =
    Number(calculation.paymentFee?.card) ||
    Number(revenueData?.expense?.paymentFee?.card) ||
    0
  const paymentFeeMobile =
    Number(calculation.paymentFee?.mobile) ||
    Number(revenueData?.expense?.paymentFee?.mobile) ||
    0
  // 淨營業收入 = 營業收入 − 金流服務費（扣除第三方支付服務費後的真實入帳）
  const netRevenue =
    Number(calculation.netRevenue?.total) ||
    Number(summary.netRevenue) ||
    operatingRevenue - paymentFee
  // 刷卡收款 / 行動支付收款（純記錄展示）
  const cardPaymentIncome = Number(income.cardPayment?.nonCash ?? 0)
  const mobilePaymentIncome = Number(income.mobilePayment?.nonCash ?? 0)

  const summaryColumns = [
    {
      title: '項目',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <span style={record.excluded ? { color: colors.text.tertiary } : undefined}>
            {text}
          </span>
          {record.excluded && (
            <Tooltip title='此項目已設定為不計入營業成本，金額僅供參考'>
              <Tag color='default' style={{ marginInlineEnd: 0 }}>
                未計入
              </Tag>
            </Tooltip>
          )}
          {record.info && (
            <Tooltip title={record.info}>
              <InfoCircleOutlined
                style={{ color: colors.text.tertiary, fontSize: 12 }}
              />
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      width: 140,
      render: (value, record) => (
        <AmountCell negative={record.negative}>
          {formatCurrency(value || 0)}
        </AmountCell>
      )
    }
  ]

  return (
    <PageContainer>
      <PageTitle>營收紀錄查詢</PageTitle>

      <FilterCard>
        <FilterRow>
          <Select
            value={dateRange}
            onChange={handleDateRangeChange}
            style={{ width: 120 }}
          >
            <Option value='daily'>日</Option>
            <Option value='monthly'>月</Option>
            <Option value='yearly'>年</Option>
          </Select>

          {dateRange === 'daily' && (
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              format='YYYY-MM-DD'
              suffixIcon={<CalendarOutlined />}
              style={{ width: 200 }}
            />
          )}

          {dateRange === 'monthly' && (
            <DatePicker
              picker='month'
              value={selectedDate}
              onChange={handleDateChange}
              format='YYYY-MM'
              suffixIcon={<CalendarOutlined />}
              style={{ width: 200 }}
            />
          )}

          {dateRange === 'yearly' && (
            <DatePicker
              picker='year'
              value={selectedDate}
              onChange={handleDateChange}
              format='YYYY'
              suffixIcon={<CalendarOutlined />}
              style={{ width: 200 }}
            />
          )}

          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            重新整理
          </Button>
        </FilterRow>
      </FilterCard>

      {/* 店家有沒有賺錢：營業收入、成本、毛利、盈餘；優先使用 calculation 公式 */}
      <SummaryCard>
        <SummaryRow gutter={[24, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title='營業收入'
              value={formatCurrency(operatingRevenue)}
              prefix={<ArrowUpOutlined style={{ color: colors.success }} />}
              valueStyle={{
                color: colors.text.primary,
                fontSize: 22,
                fontWeight: 500
              }}
            />
            <Tooltip title={calculation.operatingRevenue?.formula}>
              <Text type='secondary' style={{ fontSize: 12 }}>
                {calculation.operatingRevenue?.formula || '技術服務＋商品銷售'}
              </Text>
            </Tooltip>
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title='營業成本'
              value={formatCurrency(operatingCost)}
              prefix={<ArrowDownOutlined style={{ color: colors.error }} />}
              valueStyle={{
                color: colors.error,
                fontSize: 22,
                fontWeight: 500
              }}
            />
            <Tooltip title='進貨成本 + 退款 + 薪資 + 金流服務費 + 零用金 + 儲值折讓 + 其他（客戶使用儲值不列成本）'>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  color: colors.text.secondary
                }}
              >
                {calculation.totalExpense?.formula ||
                  calculation.operatingCost?.formula ||
                  '進貨成本 + 退款 + 薪資 + 金流服務費 + 零用金 + 儲值折讓 + 其他'}
              </div>
            </Tooltip>
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title='毛利'
              value={formatCurrency(grossProfit)}
              prefix={<DollarOutlined style={{ color: colors.secondary }} />}
              valueStyle={{
                color: grossProfit >= 0 ? colors.success : colors.error,
                fontSize: 22,
                fontWeight: 500
              }}
            />
            <Tooltip title={calculation.grossProfit?.formula}>
              <Text type='secondary' style={{ fontSize: 12 }}>
                {calculation.grossProfit?.formula || '營業收入 − 進貨成本'}
              </Text>
            </Tooltip>
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title='本期盈餘'
              value={formatCurrency(netProfit)}
              prefix={
                <DollarOutlined
                  style={{
                    color: netProfit >= 0 ? colors.success : colors.error
                  }}
                />
              }
              valueStyle={{
                color: netProfit >= 0 ? colors.success : colors.error,
                fontSize: 22,
                fontWeight: 600
              }}
            />
            <Tooltip
              title={
                calculation.netProfit?.formula ||
                '營業收入 − 營業成本（支出總計）'
              }
            >
              <Text type='secondary' style={{ fontSize: 12 }}>
                {calculation.netProfit?.formula || '營業收入 − 營業成本'}
              </Text>
            </Tooltip>
          </Col>
        </SummaryRow>
        <div
          style={{
            marginTop: spacing.sm,
            paddingTop: spacing.sm,
            borderTop: `1px solid ${colors.border}`,
            fontSize: 13,
            color: colors.text.secondary
          }}
        >
          <strong style={{ color: colors.text.primary }}>四者關係：</strong>
          營業收入 − 營業成本 = 本期盈餘；毛利 = 營業收入 −
          進貨成本（僅扣商品成本）
        </div>

        {/* 營業成本要包含哪些項目：整店統一設定，需 finance:manage 才能改 */}
        <div
          style={{
            marginTop: spacing.md,
            paddingTop: spacing.md,
            borderTop: `1px dashed ${colors.border}`
          }}
        >
          <Space wrap size={[16, 8]} align='center'>
            <span
              style={{
                fontSize: 13,
                color: colors.text.primary,
                fontWeight: 500
              }}
            >
              營業成本包含
              <Tooltip title='進貨與退款一律計入：進貨是毛利的定義（毛利 = 營業收入 − 進貨成本），退款是實際付出去的錢。其餘四項可依店家的認定自行決定。此設定全店共用，會影響所有人看到的營業成本與本期盈餘。'>
                <InfoCircleOutlined
                  style={{
                    color: colors.text.tertiary,
                    fontSize: 12,
                    marginLeft: 4
                  }}
                />
              </Tooltip>
            </span>
            <Tag color='default'>進貨</Tag>
            <Tag color='default'>退款</Tag>
            <Tag color='default'>其他</Tag>
            {COST_ITEM_OPTIONS.map((item) => (
              <Checkbox
                key={item.key}
                checked={isCostItemIncluded(item.key)}
                disabled={!canEditCostItems || savingCostItems}
                onChange={(e) =>
                  handleToggleCostItem(item.key, e.target.checked)
                }
              >
                {item.label}
              </Checkbox>
            ))}
            {savingCostItems && <Spin size='small' />}
          </Space>
          {!canEditCostItems && (
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: colors.text.tertiary
              }}
            >
              ※ 需要「財務管理」權限才能調整
            </div>
          )}
          {Object.keys(excludedCostItems).length > 0 && (
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: colors.text.secondary
              }}
            >
              未計入營業成本：
              {COST_ITEM_OPTIONS.filter(
                (item) => excludedCostItems[item.key] !== undefined
              )
                .map(
                  (item) =>
                    `${item.label} ${formatCurrency(
                      excludedCostItems[item.key]
                    )}`
                )
                .join('、')}
            </div>
          )}
        </div>

        {/* === 新增：金流服務費與真實收益（扣除刷卡／行動支付手續費後）=== */}
        {(paymentFee > 0 ||
          cardPaymentIncome > 0 ||
          mobilePaymentIncome > 0) && (
          <div
            style={{
              marginTop: spacing.md,
              paddingTop: spacing.md,
              borderTop: `1px dashed ${colors.border}`
            }}
          >
            <div
              style={{
                fontSize: 13,
                color: colors.text.primary,
                fontWeight: 500,
                marginBottom: spacing.sm
              }}
            >
              金流服務費（刷卡／行動支付手續費）
            </div>
            <SummaryRow gutter={[24, 16]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title='刷卡收款'
                  value={formatCurrency(cardPaymentIncome)}
                  valueStyle={{
                    color: colors.text.primary,
                    fontSize: 18,
                    fontWeight: 500
                  }}
                />
                <Text type='secondary' style={{ fontSize: 12 }}>
                  手續費 {formatCurrency(paymentFeeCard)}
                </Text>
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title='行動支付收款'
                  value={formatCurrency(mobilePaymentIncome)}
                  valueStyle={{
                    color: colors.text.primary,
                    fontSize: 18,
                    fontWeight: 500
                  }}
                />
                <Text type='secondary' style={{ fontSize: 12 }}>
                  手續費 {formatCurrency(paymentFeeMobile)}
                </Text>
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title='金流服務費合計'
                  value={formatCurrency(paymentFee)}
                  prefix={
                    <ArrowDownOutlined style={{ color: colors.error }} />
                  }
                  valueStyle={{
                    color: colors.error,
                    fontSize: 18,
                    fontWeight: 500
                  }}
                />
                <Tooltip title='收單行／第三方支付於撥款時扣除的服務費，會計上列「銷售費用－金流服務費」'>
                  <Text type='secondary' style={{ fontSize: 12 }}>
                    刷卡手續費 + 行動支付手續費
                  </Text>
                </Tooltip>
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title='淨營業收入（真實入帳）'
                  value={formatCurrency(netRevenue)}
                  prefix={
                    <DollarOutlined
                      style={{
                        color:
                          netRevenue >= 0 ? colors.success : colors.error
                      }}
                    />
                  }
                  valueStyle={{
                    color: netRevenue >= 0 ? colors.success : colors.error,
                    fontSize: 18,
                    fontWeight: 600
                  }}
                />
                <Tooltip title='扣除第三方支付服務費後，店家實際入帳的金額'>
                  <Text type='secondary' style={{ fontSize: 12 }}>
                    營業收入 − 金流服務費
                  </Text>
                </Tooltip>
              </Col>
            </SummaryRow>
            <div
              style={{
                marginTop: spacing.sm,
                fontSize: 12,
                color: colors.text.secondary
              }}
            >
              註：本期盈餘（{formatCurrency(netProfit)}
              ）已將金流手續費計入營業成本，故為扣除手續費後的真實收益。
            </div>
          </div>
        )}
      </SummaryCard>

      {/* 現金流：日/月/年皆顯示 */}
      {revenueData?.cashFlow && (
        <SectionCard
          title={
            <SectionTitle>
              <BankOutlined style={{ color: colors.primary }} />
              {dateRange === 'daily' ? '今日現金流' : dateRange === 'monthly' ? '本月現金流' : '本年現金流'}
            </SectionTitle>
          }
        >
          {/* 四項收入/退款：flex 等寬，自動換行 */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {[
              {
                title: '實際收款',
                value: revenueData.cashFlow.paymentReceived ?? 0,
                color: colors.success,
                breakdown: revenueData.paymentBreakdown
              },
              {
                title: '儲值收入',
                value: revenueData.cashFlow.storedValueReceived ?? 0,
                color: colors.success,
                breakdown: revenueData.storedValueBreakdown
              },
              {
                title: '堂票銷售',
                value: revenueData.cashFlow.classTicketReceived ?? 0,
                color: colors.success,
                breakdown: revenueData.classTicketBreakdown
              },
              {
                // 退回客戶儲值金不是現金支出（餘額增加、錢沒有離開櫃檯），
                // 所以不加負號、也不列入下方的淨現金
                title: '退回儲值金',
                value:
                  revenueData.cashFlow.storedValueRefunded ??
                  revenueData.cashFlow.refund ??
                  0,
                color: colors.text.secondary,
                breakdown: null,
                note: '退回客戶儲值餘額，非現金支出（不含作廢訂單）'
              }
            ].map(({ title, value, color, breakdown, note }) => (
              <div
                key={title}
                style={{
                  flex: '1 1 160px',
                  minWidth: 140,
                  padding: '12px 16px',
                  background: '#fafafa',
                  borderRadius: 8,
                  border: '1px solid #f0f0f0'
                }}
              >
                <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{title}</div>
                <div style={{ fontSize: 20, fontWeight: 600, color, lineHeight: 1.3 }}>
                  {formatCurrency(value)}
                </div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                  {breakdown
                    ? (() => {
                        const parts = [
                          `現金 $${(breakdown.cash ?? 0).toLocaleString()}`,
                          `轉帳 $${(breakdown.transfer ?? 0).toLocaleString()}`
                        ]
                        if ((breakdown.mobile ?? 0) > 0) parts.push(`行動支付 $${breakdown.mobile.toLocaleString()}`)
                        if ((breakdown.card ?? 0) > 0) parts.push(`刷卡 $${breakdown.card.toLocaleString()}`)
                        if ((breakdown.other ?? 0) > 0) parts.push(`其他 $${breakdown.other.toLocaleString()}`)
                        return parts.join(' ／ ')
                      })()
                    : note}
                </div>
              </div>
            ))}
          </div>

          {/* 淨現金：全寬突出顯示 */}
          {(() => {
            const net = revenueData.cashFlow.netCashInflow ?? 0
            const isPositive = net >= 0
            return (
              <div
                style={{
                  marginTop: 12,
                  padding: '14px 20px',
                  background: isPositive ? '#f6ffed' : '#fff2f0',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 8
                }}
              >
                <div>
                  <div style={{ fontSize: 13, color: '#555', marginBottom: 2 }}>實際入帳（淨現金）</div>
                  <div style={{ fontSize: 11, color: '#aaa' }}>收款＋儲值＋堂票</div>
                </div>
                <div style={{ fontSize: 26, fontWeight: 700, color: isPositive ? colors.success : colors.error }}>
                  {formatCurrency(net)}
                </div>
              </div>
            )
          })()}
        </SectionCard>
      )}

      {/* 應收帳款（店內分期）：只有真的有分期計畫時才出現，沒用分期的店家看不到 */}
      {revenueData?.receivables &&
        (revenueData.receivables.outstanding > 0 ||
          revenueData.receivables.collected > 0) && (
          <SectionCard
            title={
              <SectionTitle>
                <InfoCircleOutlined style={{ color: colors.warning }} />
                應收帳款（分期收款）
              </SectionTitle>
            }
          >
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {[
                {
                  title: '期末未收餘額',
                  value: revenueData.receivables.outstanding ?? 0,
                  hint: '到今天為止客人還沒繳的分期款',
                  color: colors.warning
                },
                {
                  title: '本期新增應收',
                  value: revenueData.receivables.newlyIssued ?? 0,
                  hint: '這段期間新簽的分期總額',
                  color: colors.primary
                },
                {
                  title: '本期收回',
                  value: revenueData.receivables.collected ?? 0,
                  hint: '依實際收款日計算',
                  color: colors.success
                },
                {
                  title: '逾期金額',
                  value: revenueData.receivables.overdue ?? 0,
                  hint: '已過到期日（含寬限期）仍未收',
                  color: colors.error
                }
              ].map((item) => (
                <div
                  key={item.title}
                  style={{
                    flex: '1 1 180px',
                    minWidth: 160,
                    border: '1px solid #f0f0f0',
                    borderRadius: 10,
                    padding: 14
                  }}
                >
                  <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                    {item.title}
                  </div>
                  <div
                    style={{ fontSize: 22, fontWeight: 700, color: item.color }}
                  >
                    {formatCurrency(item.value)}
                  </div>
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                    {item.hint}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: 12,
                fontSize: 12,
                color: '#8a8a8f',
                lineHeight: 1.8
              }}
            >
              營業收入是在結帳當天就全額認列的，所以「這個月營收」會比「戶頭實際進帳」多出還沒收回來的分期款。
              上面的未收餘額就是這兩個數字之間的差額。
            </div>

            {revenueData.receivables.overdueCustomers?.length > 0 && (
              <TableContainer style={{ marginTop: 16 }}>
                <Table
                  size='small'
                  rowKey='planId'
                  pagination={false}
                  scroll={{ y: 280 }}
                  dataSource={revenueData.receivables.overdueCustomers}
                  columns={[
                    { title: '客人', dataIndex: 'customerName' },
                    { title: '電話', dataIndex: 'customerPhone', width: 130 },
                    {
                      title: '逾期金額',
                      dataIndex: 'overdueAmount',
                      align: 'right',
                      render: (v) => formatCurrency(v)
                    },
                    {
                      title: '未收餘額',
                      dataIndex: 'outstanding',
                      align: 'right',
                      render: (v) => formatCurrency(v)
                    },
                    {
                      title: '最早未繳到期日',
                      dataIndex: 'oldestDueDate',
                      width: 140
                    },
                    {
                      title: '逾期天數',
                      dataIndex: 'overdueDays',
                      width: 100,
                      align: 'right',
                      render: (v) => `${v} 天`
                    }
                  ]}
                />
              </TableContainer>
            )}
          </SectionCard>
        )}

      {/* 預收款：收到的儲值金、使用的儲值金、儲值金餘額 + 預收款流動指標 */}
      <SectionCard
        title={
          <SectionTitle>
            <InfoCircleOutlined style={{ color: colors.warning }} />
            預收款（儲值金）
          </SectionTitle>
        }
      >
        <Spin spinning={loading}>
          {revenueData ? (
            <>
              <Row gutter={[24, 16]}>
                <Col xs={24} md={prepaymentUseRate != null ? 16 : 24}>
                  <TableContainer>
                    <Table
                      columns={summaryColumns}
                      dataSource={prepaymentRows}
                      rowKey='key'
                      pagination={false}
                      size='small'
                      locale={{
                        emptyText: (
                          <Empty
                            description='暫無預收款數據'
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            style={{ padding: '24px 0' }}
                          />
                        )
                      }}
                    />
                  </TableContainer>
                  <div
                    style={{
                      marginTop: spacing.md,
                      padding: spacing.md,
                      background: colors.background.secondary,
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`,
                      fontSize: 13,
                      color: colors.text.secondary,
                      lineHeight: 1.6
                    }}
                  >
                    <strong style={{ color: colors.text.primary }}>
                      關係說明（{rangeLabel}）
                    </strong>
                    <div style={{ marginTop: 8 }}>
                      <strong>查 {rangeLabel}：</strong>本期收到{' '}
                      {formatCurrency(prepaymentReceived)}
                      {prepaymentBonus > 0 && (
                        <>（含贈送後客戶餘額共增加{' '}
                          {formatCurrency(prepaymentReceivedFace)}）</>
                      )}
                      、本期使用 {formatCurrency(prepaymentUsed)}。
                      <br />
                      本期淨增加 = {formatCurrency(prepaymentReceivedFace)} −{' '}
                      {formatCurrency(prepaymentUsed)} ={' '}
                      <strong>{formatCurrency(prepaymentNetChange)}</strong>
                      <br />
                      儲值金餘額（{endDateStr}）= 期初餘額 + 本期收到 − 本期使用
                      = <strong>{formatCurrency(prepaymentBalance)}</strong>
                      <br />
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        ※ 餘額 ≠ 本期收到 −
                        本期使用；餘額需加期初餘額（從開店累計到區間開始前一天）。
                      </Text>
                    </div>
                  </div>
                </Col>
                {prepaymentUseRate != null && (
                  <Col xs={24} md={8}>
                    <div
                      style={{
                        padding: spacing.md,
                        background: colors.background.secondary,
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`
                      }}
                    >
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        預收款流動
                      </Text>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: colors.primary,
                          marginTop: 4
                        }}
                      >
                        {prepaymentUseRate}%
                      </div>
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        本期使用率（使用的儲值 ÷ 餘額增加）
                      </Text>
                    </div>
                  </Col>
                )}
              </Row>
            </>
          ) : (
            <Empty description='暫無數據' />
          )}
        </Spin>
      </SectionCard>

      <SectionCard
        title={
          <SectionTitle>
            <ArrowUpOutlined style={{ color: colors.success }} />
            收入明細
          </SectionTitle>
        }
      >
        <Spin spinning={loading}>
          {revenueData ? (
            <Row gutter={[24, 16]} wrap>
              <Col xs={24} md={14}>
                <TableContainer>
                  <Table
                    columns={summaryColumns}
                    dataSource={incomeRows}
                    rowKey='key'
                    pagination={false}
                    size='small'
                    locale={{
                      emptyText: (
                        <Empty
                          description='暫無收入數據'
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          style={{ padding: '24px 0' }}
                        />
                      )
                    }}
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            <strong>營業收入合計</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align='right'>
                            <strong>{formatCurrency(operatingRevenue)}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </TableContainer>
              </Col>
              <Col xs={24} md={10}>
                <div style={{ minHeight: 320, overflow: 'visible' }}>
                  {hasIncomeSourceData && (
                    <div style={{ marginBottom: spacing.md }}>
                      <Text
                        strong
                        style={{ fontSize: 13, color: colors.text.secondary }}
                      >
                        收入來源占比
                      </Text>
                      <ResponsiveContainer width='100%' height={200}>
                        <PieChart
                          margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
                        >
                          <Pie
                            data={incomeSourceComposition}
                            cx='50%'
                            cy='50%'
                            innerRadius={40}
                            outerRadius={65}
                            paddingAngle={2}
                            dataKey='value'
                            nameKey='name'
                            label={({ name, percent }) =>
                              `${name} ${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {incomeSourceComposition.map((entry) => (
                              <Cell key={entry.name} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{ borderRadius: 8 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {hasPaymentData && (
                    <div>
                      <Text
                        strong
                        style={{ fontSize: 13, color: colors.text.secondary }}
                      >
                        技術服務－支付組成
                      </Text>
                      <ResponsiveContainer width='100%' height={200}>
                        <PieChart
                          margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
                        >
                          <Pie
                            data={paymentComposition}
                            cx='50%'
                            cy='50%'
                            innerRadius={35}
                            outerRadius={55}
                            paddingAngle={2}
                            dataKey='value'
                            nameKey='name'
                            label={({ name, percent }) =>
                              `${name} ${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {paymentComposition.map((entry) => (
                              <Cell key={entry.name} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{ borderRadius: 8 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {!hasIncomeSourceData && !hasPaymentData && (
                    <Empty
                      description='暫無收入分析數據'
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      style={{ padding: '32px 0' }}
                    />
                  )}
                </div>
              </Col>
            </Row>
          ) : (
            <Empty description='暫無數據' />
          )}
        </Spin>
      </SectionCard>

      <SectionCard
        title={
          <SectionTitle>
            <ArrowDownOutlined style={{ color: colors.error }} />
            支出明細
          </SectionTitle>
        }
      >
        <Spin spinning={loading}>
          {revenueData ? (
            <Row gutter={[24, 16]} wrap>
              <Col xs={24} md={14}>
                <TableContainer>
                  <Table
                    columns={summaryColumns}
                    dataSource={expenseRows.map((r) => ({
                      ...r,
                      negative: true
                    }))}
                    rowKey='key'
                    pagination={false}
                    size='small'
                    locale={{
                      emptyText: (
                        <Empty
                          description='暫無支出數據'
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          style={{ padding: '24px 0' }}
                        />
                      )
                    }}
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            <strong>支出合計</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align='right'>
                            <strong style={{ color: colors.error }}>
                              {formatCurrency(totalExpense)}
                            </strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </TableContainer>
              </Col>
              <Col xs={24} md={10}>
                <div style={{ minHeight: 260, overflow: 'visible' }}>
                  {salaryToRevenue != null && (
                    <div
                      style={{
                        marginBottom: spacing.md,
                        padding: spacing.sm,
                        background: colors.background.secondary,
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`
                      }}
                    >
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        薪資負擔
                      </Text>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 600,
                          color: colors.warning,
                          marginTop: 4
                        }}
                      >
                        {salaryToRevenue}%
                      </div>
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        薪資 ÷ 營業收入
                      </Text>
                    </div>
                  )}
                  {hasExpenseCompositionData ? (
                    <div>
                      <Text
                        strong
                        style={{ fontSize: 13, color: colors.text.secondary }}
                      >
                        支出結構占比
                      </Text>
                      <ResponsiveContainer width='100%' height={290}>
                        <PieChart
                          margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
                        >
                          <Pie
                            data={expenseComposition}
                            cx='50%'
                            cy='50%'
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey='value'
                            nameKey='name'
                            label={({ name, percent }) =>
                              `${name} ${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {expenseComposition.map((entry) => (
                              <Cell key={entry.name} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{ borderRadius: 8 }}
                          />
                          <Legend verticalAlign='bottom' height={36} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Empty
                      description='暫無支出分析數據'
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      style={{ padding: '24px 0' }}
                    />
                  )}
                </div>
              </Col>
            </Row>
          ) : (
            <Empty description='暫無數據' />
          )}
        </Spin>
      </SectionCard>
    </PageContainer>
  )
}

export default RevenueRecordsPage
